import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import "./rules.css";
import "../themeupdate/themeupdate.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../sessionSlice";
import { saveRulesData } from "../../functions/saveRulesData";

const MAX_RULES = 6;
const MAX_RULE_LEN = 200;
const MAX_GAME_CONTENT = 6;
const MAX_GAME_CONTENT_LEN = 400;
const MAX_THANKYOU_LEN = 300;
const MAX_RIGHT_ANSWER_POINTS = 100;
const MAX_TOTAL_QUESTIONS = 100;
const MAX_OPTION_DESC_LEN = 50;
const MAX_TAB_NAME_LEN = 15;

const NAMED_COLORS = {
  black: "#000000",
  white: "#ffffff",
  red: "#ff0000",
  green: "#008000",
  blue: "#0000ff",
  yellow: "#ffff00",
  gray: "#808080",
  grey: "#808080",
};

const ensureArray = (val) => {
  if (Array.isArray(val)) return val;
  if (val == null || val === "") return [];
  if (typeof val === "string") {
    try {
      const p = JSON.parse(val);
      return Array.isArray(p) ? p : [];
    } catch {
      return [];
    }
  }
  return [];
};

function toColorInputValue(val) {
  if (!val || typeof val !== "string") return "#000000";
  const v = val.trim();
  if (/^#[0-9a-fA-F]{6}$/.test(v)) return v.toLowerCase();
  if (/^#[0-9a-fA-F]{8}$/.test(v)) return `#${v.slice(1, 7).toLowerCase()}`;
  const named = NAMED_COLORS[v.toLowerCase()];
  if (named) return named;
  return "#000000";
}

const clampInt = (n, min, max, fallback) => {
  const x = parseInt(String(n), 10);
  if (Number.isNaN(x)) return fallback;
  return Math.min(max, Math.max(min, x));
};

function parseStoredBool(raw, fallback = true) {
  if (raw === undefined || raw === null || raw === "") return fallback;
  if (typeof raw === "boolean") return raw;
  const s = String(raw).trim().toLowerCase();
  if (s === "true" || s === "1" || s === "yes") return true;
  if (s === "false" || s === "0" || s === "no") return false;
  return fallback;
}

const normalizeMarksInput = (raw, max) => {
  const digits = String(raw).replace(/\D/g, "");
  if (digits === "") return "";
  const n = parseInt(digits, 10);
  if (!Number.isFinite(n) || n < 1) return "";
  if (n > max) return String(max);
  return String(n);
};

const Rules = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const { currentTheme, data, status } = useSelector((state) => state.theme);

  const [loading, setLoading] = useState(false);
  const [tabName, setTabName] = useState("");
  const [rules, setRules] = useState([""]);
  const [gameContent, setGameContent] = useState([""]);
  const [thankYouText, setThankYouText] = useState("");
  const [points, setPoints] = useState("1");
  const [totalQuestion, setTotalQuestion] = useState("1");
  const [shuffle, setShuffle] = useState(true);
  const [popup, setPopup] = useState(false);
  const [thankyouToggle, setThankyouToggle] = useState(false);
  const [textColor, setTextColor] = useState("#000000");
  const [buttonColor, setButtonColor] = useState("#667eea");
  const [buttonTextColor, setButtonTextColor] = useState("#ffffff");
  const [textBgColor, setTextBgColor] = useState("#ffffff");
  const [optionOneColor, setOptionOneColor] = useState("#c7574c");
  const [optionTwoColor, setOptionTwoColor] = useState("#2e8b67");
  const [optionOneDescription, setOptionOneDescription] = useState("");
  const [optionTwoDescription, setOptionTwoDescription] = useState("");

  useEffect(() => {
    if (currentTheme) dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (!data) return;

    setTabName(String(data.tab_name ?? "").slice(0, MAX_TAB_NAME_LEN));

    const r = ensureArray(data.rules);
    setRules(r.length ? r.map(String) : [""]);

    const gc = ensureArray(data.game_content);
    setGameContent(gc.length ? gc.map(String) : [""]);

    setThankYouText(String(data.custom_text_thank_you_page ?? "").slice(0, MAX_THANKYOU_LEN));

    setPoints(String(clampInt(data.points, 1, MAX_RIGHT_ANSWER_POINTS, 1)));
    setTotalQuestion(String(clampInt(data.total_question, 1, MAX_TOTAL_QUESTIONS, 1)));
    setShuffle(parseStoredBool(data.shuffle, true));
    setPopup(parseStoredBool(data.popup, false));
    setThankyouToggle(parseStoredBool(data.thankyou_toggle, false));
    setTextColor(toColorInputValue(data.textcolor));
    setButtonColor(toColorInputValue(data.button_color ?? "#667eea"));
    setButtonTextColor(
      toColorInputValue(data.button_Textcolor ?? data.button_text_color ?? "#ffffff")
    );
    setTextBgColor(toColorInputValue(data.text_bg_color ?? data.icon_bg_color ?? "#ffffff"));
    setOptionOneColor(toColorInputValue(data.option_one_color ?? "#c7574c"));
    setOptionTwoColor(toColorInputValue(data.option_two_color ?? "#2e8b67"));
    setOptionOneDescription(String(data.option_one_description ?? "").slice(0, MAX_OPTION_DESC_LEN));
    setOptionTwoDescription(String(data.option_two_description ?? "").slice(0, MAX_OPTION_DESC_LEN));
  }, [data]);

  const handleRuleChange = (idx, val) => {
    if (val.length > MAX_RULE_LEN) {
      Swal.fire("Limit", `Each rule can be at most ${MAX_RULE_LEN} characters.`, "warning");
      val = val.slice(0, MAX_RULE_LEN);
    }
    const arr = [...rules];
    arr[idx] = val;
    setRules(arr);
  };

  const handleAddRule = () => {
    if (rules.length >= MAX_RULES) {
      Swal.fire("Limit", `Up to ${MAX_RULES} rules.`, "warning");
      return;
    }
    setRules([...rules, ""]);
  };

  const handleRemoveRule = (idx) => {
    if (rules.length <= 1) {
      Swal.fire("Required", "At least one rule is required.", "warning");
      return;
    }
    setRules(rules.filter((_, i) => i !== idx));
  };

  const handleGameContentChange = (idx, val) => {
    if (val.length > MAX_GAME_CONTENT_LEN) {
      Swal.fire("Limit", `Each content item can be at most ${MAX_GAME_CONTENT_LEN} characters.`, "warning");
      val = val.slice(0, MAX_GAME_CONTENT_LEN);
    }
    const arr = [...gameContent];
    arr[idx] = val;
    setGameContent(arr);
  };

  const handleAddGameContent = () => {
    if (gameContent.length >= MAX_GAME_CONTENT) {
      Swal.fire("Limit", `Up to ${MAX_GAME_CONTENT} content items.`, "warning");
      return;
    }
    setGameContent([...gameContent, ""]);
  };

  const handleRemoveGameContent = (idx) => {
    if (gameContent.length <= 1) {
      Swal.fire("Required", "At least one content item is required.", "warning");
      return;
    }
    setGameContent(gameContent.filter((_, i) => i !== idx));
  };

  const validate = () => {
    const trimmedTabName = tabName.trim();
    if (!trimmedTabName) {
      return { ok: false, msg: "Tab name is required." };
    }
    if (trimmedTabName.length > MAX_TAB_NAME_LEN) {
      return { ok: false, msg: `Tab name must be ${MAX_TAB_NAME_LEN} characters or less.` };
    }

    const trimmedRules = rules.map((r) => r.trim()).filter(Boolean);
    if (trimmedRules.length === 0) return { ok: false, msg: "At least one rule is required." };
    for (const r of trimmedRules) {
      if (r.length > MAX_RULE_LEN) return { ok: false, msg: "Each rule must be 200 characters or less." };
    }

    const trimmedGameContent = gameContent.map((c) => c.trim()).filter(Boolean);
    if (trimmedGameContent.length === 0) {
      return { ok: false, msg: "At least one game page content item is required." };
    }
    for (const c of trimmedGameContent) {
      if (c.length > MAX_GAME_CONTENT_LEN) {
        return { ok: false, msg: `Each game content item must be ${MAX_GAME_CONTENT_LEN} characters or less.` };
      }
    }

    const trimmedThankYou = thankYouText.trim();
    if (trimmedThankYou.length > MAX_THANKYOU_LEN) {
      return { ok: false, msg: `Thank you page text must be ${MAX_THANKYOU_LEN} characters or less.` };
    }

    // Both descriptions are optional — empty is fine, only the length is capped.
    const trimmedOptionOneDescription = optionOneDescription.trim();
    if (trimmedOptionOneDescription.length > MAX_OPTION_DESC_LEN) {
      return { ok: false, msg: `Option one description must be ${MAX_OPTION_DESC_LEN} characters or less.` };
    }
    const trimmedOptionTwoDescription = optionTwoDescription.trim();
    if (trimmedOptionTwoDescription.length > MAX_OPTION_DESC_LEN) {
      return { ok: false, msg: `Option two description must be ${MAX_OPTION_DESC_LEN} characters or less.` };
    }

    const pointsNum = parseInt(points, 10);
    if (!points || Number.isNaN(pointsNum) || pointsNum <= 0) {
      return { ok: false, msg: "Correct answer points must be greater than 0." };
    }

    const totalQuestionNum = parseInt(totalQuestion, 10);
    if (!totalQuestion || Number.isNaN(totalQuestionNum) || totalQuestionNum <= 0) {
      return { ok: false, msg: "Number of questions to display must be greater than 0." };
    }

    return {
      ok: true,
      payload: {
        tab_name: trimmedTabName,
        rules: trimmedRules,
        game_content: trimmedGameContent,
        custom_text_thank_you_page: trimmedThankYou,
        points: clampInt(points, 1, MAX_RIGHT_ANSWER_POINTS, 1),
        total_question: clampInt(totalQuestion, 1, MAX_TOTAL_QUESTIONS, 1),
        shuffle,
        popup,
        thankyou_toggle: thankyouToggle,
        textcolor: textColor,
        button_color: buttonColor,
        button_Textcolor: buttonTextColor,
        text_bg_color: textBgColor,
        option_one_color: optionOneColor,
        option_two_color: optionTwoColor,
        option_one_description: trimmedOptionOneDescription,
        option_two_description: trimmedOptionTwoDescription,
        // Preserve existing fields that aren't being edited
        landing_page_title: String(data?.landing_page_title ?? "").trim(),
        wrong_points: clampInt(data?.wrong_points, 1, Number.MAX_SAFE_INTEGER, 1),
        icon_bg_color: textBgColor,
        box_color: data?.box_color ?? data?.boxcolor ?? "#1e293b",
        use_timeout: parseStoredBool(data?.use_timeout, true),
        question_timeout_minutes: clampInt(data?.question_timeout_minutes, 0, 59, 1),
        question_timeout_seconds: clampInt(data?.question_timeout_seconds, 0, 59, 30),
        main: ensureArray(data?.main),
      },
    };
  };

  const handleSave = async () => {
    const v = validate();
    if (!v.ok) {
      Swal.fire("Error", v.msg, "error");
      return;
    }

    setLoading(true);
    try {
      await saveRulesData({
        payload: {
          data: v.payload,
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
      await Swal.fire("Success", "Data updated", "success");
      navigate("/admin/themeupdate");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save", "error");
    } finally {
      setLoading(false);
    }
  };

  if (status === "loading" && !data) {
    return (
      <div className="rules-page-new">
        <div className="themeupdate-loading">Loading...</div>
      </div>
    );
  }

  return (
    <>
      <div className="back-button-holder">
        <button type="button" className="back-button" onClick={() => navigate("/admin")}>
          <i className="fa-solid fa-arrow-left" /> Back
        </button>
      </div>

      <div className="themeupdate-row rules-php-layout">
        <div className="themeupdate-col rules-php-col">
          <div className="themeupdate-card rules-php-card rules-php-card--left">
            <div className="themeupdate-card-body rules-php-card-body">
              <h4 className="themeupdate-label rules-php-card-title">Tab Name</h4>
              <input
                type="text"
                className="themeupdate-input"
                placeholder="e.g. Myth Fact"
                maxLength={MAX_TAB_NAME_LEN}
                value={tabName}
                onChange={(e) => setTabName(e.target.value.slice(0, MAX_TAB_NAME_LEN))}
              />
              <p className="themeupdate-muted">
                <code>* Shown as the browser tab title ({MAX_TAB_NAME_LEN} characters max, required)</code>
              </p>

              <h4 className="themeupdate-label rules-php-card-title">Custom Text Thank You Page</h4>
              <input
                type="text"
                className="themeupdate-input rules-php-thankyou-input"
                placeholder="Thank you page text"
                maxLength={MAX_THANKYOU_LEN}
                value={thankYouText}
                onChange={(e) => setThankYouText(e.target.value.slice(0, MAX_THANKYOU_LEN))}
              />
              <p className="themeupdate-muted">
                <code>* This text will reflect on the thank you page ({MAX_THANKYOU_LEN} characters max)</code>
              </p>

              <h4 className="themeupdate-label rules-php-card-title">Add Custom Rules</h4>
              <div className="themeupdate-rules-repeater">
                {rules.map((rule, i) => (
                  <div key={i} className="themeupdate-rule-row rules-php-repeater-row">
                    <textarea
                      className="themeupdate-input rules-php-textarea rules-php-repeater-textarea"
                      placeholder="Rules"
                      maxLength={MAX_RULE_LEN}
                      value={rule}
                      onChange={(e) => handleRuleChange(i, e.target.value)}
                    />
                    <button
                      type="button"
                      className="themeupdate-btn themeupdate-btn-danger"
                      onClick={() => handleRemoveRule(i)}
                    >
                      <i className="fa-solid fa-times" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="themeupdate-btn themeupdate-btn-primary themeupdate-add-rule"
                  onClick={handleAddRule}
                >
                  <i className="fa-solid fa-plus" /> Add new rule
                </button>
                <p className="themeupdate-muted">
                  <code>
                    * Up to {MAX_RULES} rules, at least 1 required, {MAX_RULE_LEN} characters each
                  </code>
                </p>
              </div>

              <h4 className="themeupdate-label rules-php-card-title">Game Page Content</h4>
              <div className="themeupdate-rules-repeater">
                {gameContent.map((content, i) => (
                  <div key={i} className="themeupdate-rule-row rules-php-repeater-row">
                    <textarea
                      className="themeupdate-input rules-php-textarea rules-php-repeater-textarea"
                      placeholder="Game content"
                      maxLength={MAX_GAME_CONTENT_LEN}
                      value={content}
                      onChange={(e) => handleGameContentChange(i, e.target.value)}
                    />
                    <button
                      type="button"
                      className="themeupdate-btn themeupdate-btn-danger"
                      onClick={() => handleRemoveGameContent(i)}
                    >
                      <i className="fa-solid fa-times" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="themeupdate-btn themeupdate-btn-primary themeupdate-add-rule"
                  onClick={handleAddGameContent}
                >
                  <i className="fa-solid fa-plus" /> Add Content
                </button>
                <p className="themeupdate-muted">
                  <code>
                    * Up to {MAX_GAME_CONTENT} content items, at least 1 required, {MAX_GAME_CONTENT_LEN} characters each
                  </code>
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="themeupdate-col rules-php-col">
          <div className="themeupdate-card rules-php-card rules-php-card--right">
            <div className="themeupdate-card-body rules-php-card-body">
              <div className="rules-php-marks-row">
                <label htmlFor="rules-points" className="rules-php-marks-label">
                  Total marks per right answer (max {MAX_RIGHT_ANSWER_POINTS})
                </label>
                <input
                  id="rules-points"
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  maxLength={3}
                  className="themeupdate-input rules-php-number"
                  value={points}
                  onChange={(e) => setPoints(normalizeMarksInput(e.target.value, MAX_RIGHT_ANSWER_POINTS))}
                  onBlur={() => {
                    const n = parseInt(points, 10);
                    if (points === "" || Number.isNaN(n) || n < 1) setPoints("1");
                    else setPoints(String(clampInt(n, 1, MAX_RIGHT_ANSWER_POINTS, 1)));
                  }}
                />
              </div>

              <div className="rules-php-marks-row">
                <label htmlFor="rules-total-question" className="rules-php-marks-label">
                  No of questions to display
                </label>
                <input
                  id="rules-total-question"
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  maxLength={3}
                  className="themeupdate-input rules-php-number"
                  value={totalQuestion}
                  onChange={(e) => setTotalQuestion(normalizeMarksInput(e.target.value, MAX_TOTAL_QUESTIONS))}
                  onBlur={() => {
                    const n = parseInt(totalQuestion, 10);
                    if (totalQuestion === "" || Number.isNaN(n) || n < 1) setTotalQuestion("1");
                    else setTotalQuestion(String(clampInt(n, 1, MAX_TOTAL_QUESTIONS, 1)));
                  }}
                />
              </div>

              <div className="rules-php-marks-row rules-php-toggle-row">
                <label htmlFor="rules-shuffle" className="rules-php-marks-label">
                  Shuffle Questions
                </label>
                <label className="rules-php-toggle">
                  <input
                    id="rules-shuffle"
                    type="checkbox"
                    checked={shuffle}
                    onChange={(e) => setShuffle(e.target.checked)}
                  />
                  <span className="rules-php-toggle-slider" />
                </label>
              </div>

              <div className="rules-php-marks-row rules-php-toggle-row">
                <label htmlFor="rules-popup" className="rules-php-marks-label">
                  Popup
                </label>
                <label className="rules-php-toggle">
                  <input
                    id="rules-popup"
                    type="checkbox"
                    checked={popup}
                    onChange={(e) => setPopup(e.target.checked)}
                  />
                  <span className="rules-php-toggle-slider" />
                </label>
              </div>

              <div className="rules-php-marks-row rules-php-toggle-row">
                <label htmlFor="rules-thankyou-toggle" className="rules-php-marks-label">
                  Enable Thank you message
                </label>
                <label className="rules-php-toggle">
                  <input
                    id="rules-thankyou-toggle"
                    type="checkbox"
                    checked={thankyouToggle}
                    onChange={(e) => setThankyouToggle(e.target.checked)}
                  />
                  <span className="rules-php-toggle-slider" />
                </label>
              </div>

              <div className="rules-php-marks-row rules-php-color-row">
                <label htmlFor="rules-button-color" className="rules-php-marks-label">
                  Button color
                </label>
                <input
                  id="rules-button-color"
                  type="color"
                  className="themeupdate-color-input rules-php-color-input"
                  value={buttonColor}
                  onChange={(e) => setButtonColor(e.target.value)}
                />
              </div>

              <div className="rules-php-marks-row rules-php-color-row">
                <label htmlFor="rules-button-text-color" className="rules-php-marks-label">
                  Button text color
                </label>
                <input
                  id="rules-button-text-color"
                  type="color"
                  className="themeupdate-color-input rules-php-color-input"
                  value={buttonTextColor}
                  onChange={(e) => setButtonTextColor(e.target.value)}
                />
              </div>

              <div className="rules-php-marks-row rules-php-color-row">
                <label htmlFor="rules-text-color" className="rules-php-marks-label">
                  Text color
                </label>
                <input
                  id="rules-text-color"
                  type="color"
                  className="themeupdate-color-input rules-php-color-input"
                  value={textColor}
                  onChange={(e) => setTextColor(e.target.value)}
                />
              </div>

              <div className="rules-php-marks-row rules-php-color-row">
                <label htmlFor="rules-text-bg-color" className="rules-php-marks-label">
                  Question background color
                </label>
                <input
                  id="rules-text-bg-color"
                  type="color"
                  className="themeupdate-color-input rules-php-color-input"
                  value={textBgColor}
                  onChange={(e) => setTextBgColor(e.target.value)}
                />
              </div>

              <div className="rules-php-marks-row rules-php-color-row">
                <label htmlFor="rules-option-one-color" className="rules-php-marks-label">
                  Option one color
                </label>
                <input
                  id="rules-option-one-color"
                  type="color"
                  className="themeupdate-color-input rules-php-color-input"
                  value={optionOneColor}
                  onChange={(e) => setOptionOneColor(e.target.value)}
                />
              </div>

              <div className="rules-php-marks-row rules-php-color-row">
                <label htmlFor="rules-option-two-color" className="rules-php-marks-label">
                  Option two color
                </label>
                <input
                  id="rules-option-two-color"
                  type="color"
                  className="themeupdate-color-input rules-php-color-input"
                  value={optionTwoColor}
                  onChange={(e) => setOptionTwoColor(e.target.value)}
                />
              </div>

              <div className="rules-php-marks-row">
                <label htmlFor="rules-option-one-description" className="rules-php-marks-label">
                  Option one description (optional, max {MAX_OPTION_DESC_LEN})
                </label>
                <input
                  id="rules-option-one-description"
                  type="text"
                  autoComplete="off"
                  maxLength={MAX_OPTION_DESC_LEN}
                  className="themeupdate-input"
                  value={optionOneDescription}
                  onChange={(e) => setOptionOneDescription(e.target.value.slice(0, MAX_OPTION_DESC_LEN))}
                />
              </div>

              <div className="rules-php-marks-row">
                <label htmlFor="rules-option-two-description" className="rules-php-marks-label">
                  Option two description (optional, max {MAX_OPTION_DESC_LEN})
                </label>
                <input
                  id="rules-option-two-description"
                  type="text"
                  autoComplete="off"
                  maxLength={MAX_OPTION_DESC_LEN}
                  className="themeupdate-input"
                  value={optionTwoDescription}
                  onChange={(e) => setOptionTwoDescription(e.target.value.slice(0, MAX_OPTION_DESC_LEN))}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="rules-action-holder">
        <button type="button" className="save-and-continue" onClick={handleSave} disabled={loading}>
          Save
        </button>
      </div>
    </>
  );
};

export default Rules;
